import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Form, Input, Modal, Pagination, Popconfirm,
  Space, Table, Tooltip,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { ColumnsType } from 'antd/lib/table';
import BreadCrumbs from '../../../common/components/Breadcrumbs';
import { Action, Page } from '../../../common/constants/pageAction';
import ability from '../../../common/ability';
import snPublisherAliasService, { ISnPublisherAlias } from '../../../services/cmdb/snPublisherAlias/snPublisherAlias.service';

const SnPublisherAlias: React.FC = () => {
  const [data, setData] = useState<ISnPublisherAlias[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [editTarget, setEditTarget] = useState<ISnPublisherAlias | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await snPublisherAliasService.list(page, limit);
      setData(result.data);
      setTotal(result.count);
    } catch { /* handled by request interceptor */ } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditTarget(null);
    form.resetFields();
    setModalVisible(true);
  };

  const openEdit = (alias: ISnPublisherAlias) => {
    setEditTarget(alias);
    form.setFieldsValue({ alias_name: alias.alias_name, canonical_name: alias.canonical_name });
    setModalVisible(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editTarget) {
        await snPublisherAliasService.update(editTarget.id, values.alias_name, values.canonical_name);
        toast.success('Alias updated.');
      } else {
        await snPublisherAliasService.create(values.alias_name, values.canonical_name);
        toast.success('Alias created.');
      }
      setModalVisible(false);
      load();
    } catch { /* handled by request interceptor */ } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await snPublisherAliasService.remove(id);
      toast.success('Alias deleted.');
      load();
    } catch { /* handled by request interceptor */ }
  };

  const columns: ColumnsType<ISnPublisherAlias> = [
    {
      title: 'Alias (as seen in inventory)',
      dataIndex: 'alias_name',
      key: 'alias_name',
      sorter: (a, b) => a.alias_name.localeCompare(b.alias_name),
    },
    {
      title: 'Canonical Name (normalizes to)',
      dataIndex: 'canonical_name',
      key: 'canonical_name',
      sorter: (a, b) => a.canonical_name.localeCompare(b.canonical_name),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (val: string) => new Date(val).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          {ability.can(Action.Update, Page.CmdbSnPublisherAliases) && (
            <Tooltip title="Edit">
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
            </Tooltip>
          )}
          {ability.can(Action.Delete, Page.CmdbSnPublisherAliases) && (
            <Popconfirm title="Delete this alias?" onConfirm={() => handleDelete(record.id)}>
              <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="sqlServer">
      <div className="title-block">
        <h4 className="p-0">
          <BreadCrumbs pageName={Page.CmdbSnPublisherAliases} />
        </h4>
      </div>

      <div className="main-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          {ability.can(Action.Add, Page.CmdbSnPublisherAliases) && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
              Add Alias
            </Button>
          )}
        </div>

        <Table<ISnPublisherAlias>
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No publisher aliases defined.' }}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={limit}
            total={total}
            showSizeChanger={false}
            showTotal={(t) => `${t} aliases`}
            onChange={setPage}
          />
        </div>
      </div>

      <Modal
        title={editTarget ? 'Edit Publisher Alias' : 'Add Publisher Alias'}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        okText="Save"
        okButtonProps={{ loading: saving }}
        centered
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="alias_name"
            label="Alias Name (as it appears in inventory)"
            rules={[{ required: true, message: 'Alias name is required' }]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item
            name="canonical_name"
            label="Canonical Name (normalize to this value)"
            rules={[{ required: true, message: 'Canonical name is required' }]}
          >
            <Input maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SnPublisherAlias;
